import axios from 'axios';

/**
 * Video streaming modes, mirroring the backend's Python schemas.py.
 * @readonly
 * @enum {string}
 */
export const VideoStreamingMode = Object.freeze({
  SYNC: 'sync',
  ASYNC: 'async',
  FLUO: 'fluo',
});

/**
 * @typedef {Object} StreamStartRequest
 * @property {string} [mode] one of VideoStreamingMode
 * @property {number} selected_chamber
 * @property {number|null} [sync_trigger_angle]
 * @property {number|null} [brightness]
 */

/**
 * @typedef {Object} StreamStartResponse
 * @property {string} stream_id
 * @property {string} mjpeg_url
 */

/**
 * @typedef {Object} StreamChangeRequest
 * @property {number|null} [selected_chamber]
 * @property {number|null} [sync_trigger_angle]
 * @property {number|null} [brightness]
 */

/**
 * @typedef {Object} StreamStatusResponse
 * @property {string} stream_id
 * @property {boolean} is_active
 * @property {number} chamber
 * @property {string} mode
 * @property {number} frame_count
 * @property {number} fps
 */

// Initialize axios instance — desktop backend (device + video streaming)
const DESKTOP_DEFAULT_PORT = import.meta.env.DEV ? '8000' : '8765';
const BACKEND_URL = `${import.meta.env.VITE_BACKEND_URL || `http://127.0.0.1:${DESKTOP_DEFAULT_PORT}`}/desktop`;

export { BACKEND_URL };

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Extracts the backend's error detail from a failed axios call. */
const toError = (error, fallback) =>
  new Error(error.response?.data?.detail || error.message || fallback);

// Video Streaming API
export const videoStreamingApi = {
  /**
   * @param {StreamStartRequest} request
   * @returns {Promise<StreamStartResponse>}
   */
  async start(request) {
    try {
      const response = await api.post('/videoStreaming/streams/start', request);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to start video streaming');
    }
  },

  /**
   * @param {string} [streamId]
   * @returns {Promise<void>}
   */
  async stop(streamId) {
    try {
      await api.post('/videoStreaming/streams/stop', { stream_id: streamId ?? null });
    } catch (error) {
      throw toError(error, 'Failed to stop video streaming');
    }
  },

  /**
   * @param {string} streamId
   * @param {StreamChangeRequest} request
   * @returns {Promise<StreamStartResponse>}
   */
  async changeSetup(streamId, request) {
    try {
      const response = await api.post(`/videoStreaming/streams/${streamId}/setup`, request);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to change streaming setup');
    }
  },
};

// Convenience functions for common operations
export const streamingHelpers = {
  /**
   * @param {number} chamber
   * @param {{ syncTriggerAngle?: number, brightness?: number }} [options]
   * @returns {Promise<StreamStartResponse>}
   */
  async startSync(chamber, options) {
    return videoStreamingApi.start({
      mode: VideoStreamingMode.SYNC,
      selected_chamber: chamber,
      sync_trigger_angle: options?.syncTriggerAngle ?? null,
      brightness: options?.brightness ?? null,
    });
  },

  /**
   * @param {number} chamber
   * @returns {Promise<StreamStartResponse>}
   */
  async startAsync(chamber) {
    return videoStreamingApi.start({
      mode: VideoStreamingMode.ASYNC,
      selected_chamber: chamber,
    });
  },

  /**
   * @param {number} chamber
   * @returns {Promise<StreamStartResponse>}
   */
  async startFluo(chamber) {
    return videoStreamingApi.start({
      mode: VideoStreamingMode.FLUO,
      selected_chamber: chamber,
    });
  },
};

export default api;
